import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";

import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog.js";
import { buttonVariants } from "../ui/button.js";
import {
  CLI_INSTALL_COMMANDS,
  INSTALL_SHELL_LABELS,
  detectInstallShell,
  useLatestDmgLinks,
  type InstallShell,
} from "./desktopDownloads.js";

const CLI_NPM_COMMAND = "npm install -g @yagni-app/code";

const MANAGED_MAC_FIX_COMMAND = [
  "mkdir -p ~/.npm-global",
  "npm config set prefix ~/.npm-global",
  "echo 'export PATH=\"$HOME/.npm-global/bin:$PATH\"' >> ~/.zshrc",
  "source ~/.zshrc",
  "npm install -g @yagni-app/code",
].join("\n");

/**
 * The "Get YAGNI Code" dialog (Settings, You): direct installer downloads for
 * the macOS and Windows builds plus the CLI install path. Apple Silicon vs
 * Intel cannot be told apart from the browser (navigator.platform says
 * "MacIntel" on both), so every build gets a clearly labeled button and a
 * one-line pointer instead of a guess.
 */
export function DownloadCodeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const links = useLatestDmgLinks();
  const [copied, setCopied] = useState<"install" | "fix" | null>(null);
  // Lead with the installer for the visitor's own OS family; the other shell's
  // command stays one line below for the person setting up a different box.
  const [shell] = useState<InstallShell>(() => detectInstallShell());
  const otherShell: InstallShell = shell === "posix" ? "powershell" : "posix";
  const installCommand = CLI_INSTALL_COMMANDS[shell];

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copyCommand(which: "install" | "fix", text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      // clipboard unavailable; the command stays selectable text
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="download-code-dialog">
        <DialogTitle style={{ fontFamily: "var(--font-display)" }}>Get YAGNI Code</DialogTitle>
        <DialogDescription>
          The coding agent your engineers run, connected to this workspace.
        </DialogDescription>

        <div className="mt-lg">
          <div
            className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-tertiary)]"
            style={{ fontFamily: "var(--font-fact)" }}
          >
            Desktop app · macOS
          </div>
          <div className="mt-sm flex flex-wrap gap-sm">
            <a
              className={buttonVariants({ variant: "primary", size: "sm" })}
              href={links.aarch64}
              data-testid="download-dmg-aarch64"
            >
              Download for Apple Silicon
            </a>
            {links.x86_64 && (
              <a
                className={buttonVariants({ variant: "secondary", size: "sm" })}
                href={links.x86_64}
                data-testid="download-dmg-x86_64"
              >
                Download for Intel Mac
              </a>
            )}
          </div>
          <p className="mt-sm text-[12px] text-[var(--text-secondary)]">
            {links.x86_64
              ? "Not sure which? Apple menu > About This Mac shows your chip."
              : "Apple Silicon and macOS 13 or later."}
          </p>
        </div>

        <div className="mt-lg">
          <div
            className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-tertiary)]"
            style={{ fontFamily: "var(--font-fact)" }}
          >
            Desktop app · Windows
          </div>
          <div className="mt-sm flex flex-wrap gap-sm">
            <a
              className={buttonVariants({ variant: "primary", size: "sm" })}
              href={links.windows}
              data-testid="download-windows-x86_64"
            >
              Download for Windows
            </a>
          </div>
          <p className="mt-sm text-[12px] text-[var(--text-secondary)]">
            64-bit Windows 10 or later. On Arm devices, use the CLI below. The installer is not yet
            code-signed: if SmartScreen warns, choose More info, then Run anyway.
          </p>
        </div>

        <div className="mt-lg">
          <div
            className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-tertiary)]"
            style={{ fontFamily: "var(--font-fact)" }}
          >
            CLI · anywhere Node 22+ runs
          </div>
          <div className="mt-sm flex items-center gap-sm rounded-[var(--radius-control)] bg-[var(--bg-code)] border border-[var(--print-border)] px-md py-sm">
            <code
              className="flex-1 min-w-0 text-[12px] text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-fact)" }}
              data-testid="cli-install-command"
            >
              {installCommand}
            </code>
            <button
              type="button"
              onClick={() => void copyCommand("install", installCommand)}
              aria-label={copied === "install" ? "Copied" : "Copy install command"}
              className="shrink-0 rounded-sm p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            >
              {copied === "install" ? (
                <Check size={14} aria-hidden />
              ) : (
                <Copy size={14} aria-hidden />
              )}
            </button>
          </div>
          <p className="mt-sm text-[12px] text-[var(--text-secondary)]">
            {INSTALL_SHELL_LABELS[shell]}. On {INSTALL_SHELL_LABELS[otherShell]}:{" "}
            <code
              style={{ fontFamily: "var(--font-fact)" }}
              data-testid="cli-install-command-other"
            >
              {CLI_INSTALL_COMMANDS[otherShell]}
            </code>
          </p>
          <p className="mt-sm text-[12px] text-[var(--text-secondary)]">
            The installer handles restricted npm permissions on managed machines automatically.
            Prefer npm? <code style={{ fontFamily: "var(--font-fact)" }}>{CLI_NPM_COMMAND}</code>{" "}
            works too.
          </p>
          <p className="mt-sm text-[12px] text-[var(--text-secondary)]">
            Then run <code style={{ fontFamily: "var(--font-fact)" }}>yagni login</code> and enter
            the device code under Settings,{" "}
            <Link
              to="/settings/you"
              className="underline text-[var(--text-primary)]"
              onClick={() => onOpenChange(false)}
            >
              You
            </Link>
            .
          </p>
          <details className="mt-sm" data-testid="managed-mac-help">
            <summary className="cursor-pointer text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              Install failed with an EACCES permission error?
            </summary>
            <p className="mt-sm text-[12px] text-[var(--text-secondary)]">
              Common on company-managed Macs: npm is pointing at a system folder your account cannot
              write to. Point it at a folder you own, then install again. No admin rights needed.
            </p>
            <div className="mt-sm flex items-start gap-sm rounded-[var(--radius-control)] bg-[var(--bg-code)] border border-[var(--print-border)] px-md py-sm">
              <code
                className="flex-1 min-w-0 whitespace-pre-wrap text-[12px] text-[var(--text-primary)]"
                style={{ fontFamily: "var(--font-fact)" }}
              >
                {MANAGED_MAC_FIX_COMMAND}
              </code>
              <button
                type="button"
                onClick={() => void copyCommand("fix", MANAGED_MAC_FIX_COMMAND)}
                aria-label={copied === "fix" ? "Copied" : "Copy fix commands"}
                className="shrink-0 rounded-sm p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              >
                {copied === "fix" ? (
                  <Check size={14} aria-hidden />
                ) : (
                  <Copy size={14} aria-hidden />
                )}
              </button>
            </div>
          </details>
        </div>
      </DialogContent>
    </Dialog>
  );
}
